// src/pages/RevenueAnalytics.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ExportButton from '../components/ExportButton';

function RevenueAnalytics() {
  const [revenueData, setRevenueData] = useState({
    total: 0,
    today: 0,
    thisWeek: 0,
    thisMonth: 0,
    byVehicle: [],
    byDay: [],
    byHour: [],
    recentTransactions: [],
  });
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('month');

  const API_URL = 'https://boda-backend-wuea.onrender.com/api/admin';

  useEffect(() => {
    fetchRevenueData();
  }, [timeRange]);

  const fetchRevenueData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch all rides
      const response = await axios.get(`${API_URL}/all-rides`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      const rides = response.data.rides || [];
      const completedRides = rides.filter(r => r.status === 'completed');
      
      // Calculate totals
      const total = completedRides.reduce((sum, r) => sum + (r.total_fare || 0), 0);
      
      // Today
      const today = new Date().toISOString().split('T')[0];
      const todayRides = completedRides.filter(r => r.created_at?.startsWith(today));
      const todayRevenue = todayRides.reduce((sum, r) => sum + (r.total_fare || 0), 0);
      
      // This week
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      const weekRides = completedRides.filter(r => new Date(r.created_at) >= weekAgo);
      const weekRevenue = weekRides.reduce((sum, r) => sum + (r.total_fare || 0), 0);
      
      // This month
      const monthAgo = new Date();
      monthAgo.setDate(monthAgo.getDate() - 30);
      const monthRides = completedRides.filter(r => new Date(r.created_at) >= monthAgo);
      const monthRevenue = monthRides.reduce((sum, r) => sum + (r.total_fare || 0), 0);
      
      // By vehicle type
      const vehicleRevenue = {};
      completedRides.forEach(r => {
        const type = r.vehicle_type || 'boda';
        vehicleRevenue[type] = (vehicleRevenue[type] || 0) + (r.total_fare || 0);
      });
      
      // By day (last 30 days)
      const dayRevenue = {};
      const last30Days = [];
      for (let i = 29; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        const dayRides = completedRides.filter(r => r.created_at?.startsWith(dateStr));
        const revenue = dayRides.reduce((sum, r) => sum + (r.total_fare || 0), 0);
        last30Days.push({ date: dateStr, revenue, rides: dayRides.length });
      }
      
      // By hour
      const hourRevenue = {};
      for (let h = 0; h < 24; h++) {
        hourRevenue[h] = 0;
      }
      completedRides.forEach(r => {
        if (r.created_at) {
          const hour = new Date(r.created_at).getHours();
          hourRevenue[hour] = (hourRevenue[hour] || 0) + (r.total_fare || 0);
        }
      });
      
      // Recent transactions
      const recent = completedRides.slice(0, 20).map(r => ({
        id: r.id,
        rider: r.customer_name || 'Rider',
        driver: r.driver_name || 'Driver',
        amount: r.total_fare || 0,
        date: r.created_at,
        status: r.status,
        vehicle: r.vehicle_type || 'boda',
      }));
      
      setRevenueData({
        total,
        today: todayRevenue,
        thisWeek: weekRevenue,
        thisMonth: monthRevenue,
        byVehicle: Object.entries(vehicleRevenue).map(([type, amount]) => ({ type, amount })),
        byDay: last30Days,
        byHour: Object.entries(hourRevenue).map(([hour, amount]) => ({ hour: parseInt(hour), amount })),
        recentTransactions: recent,
      });
      
    } catch (error) {
      console.error('Error fetching revenue data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getVehicleLabel = (type) => {
    const labels = {
      boda: '🛵 Boda',
      boda_electric: '⚡ Boda Electric',
      car: '🚗 Car',
      car_xl: '🚙 Car XL',
    };
    return labels[type] || type;
  };

  const getExportData = () => {
    return revenueData.recentTransactions.map(t => ({
      'Ride ID': t.id,
      Rider: t.rider,
      Driver: t.driver,
      Amount: t.amount,
      Vehicle: t.vehicle,
      Status: t.status,
      Date: new Date(t.date).toLocaleDateString(),
    }));
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '40px', marginBottom: '16px' }}>🔄</div>
          <div style={{ fontSize: '18px', color: '#64748b' }}>Loading revenue data...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '0 0 20px 0' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '12px',
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>Revenue Analytics</h1>
          <div style={{ display: 'flex', gap: '16px', marginTop: '4px', fontSize: '14px', color: '#64748b' }}>
            <span>💰 Total: UGX {revenueData.total.toLocaleString()}</span>
            <span style={{ color: '#10b981' }}>📈 Today: UGX {revenueData.today.toLocaleString()}</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              backgroundColor: 'white',
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
            <option value="quarter">Last 90 Days</option>
          </select>
          <ExportButton 
            data={getExportData()} 
            filename="revenue_export" 
            label="Export CSV"
          />
          <button
            onClick={fetchRevenueData}
            style={{
              padding: '10px 20px',
              backgroundColor: '#f97316',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '16px',
        marginBottom: '24px',
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '16px 20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}>
          <div style={{ fontSize: '12px', color: '#64748b' }}>Total Revenue</div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#f97316' }}>
            UGX {revenueData.total.toLocaleString()}
          </div>
        </div>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '16px 20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}>
          <div style={{ fontSize: '12px', color: '#64748b' }}>Today's Revenue</div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#10b981' }}>
            UGX {revenueData.today.toLocaleString()}
          </div>
        </div>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '16px 20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}>
          <div style={{ fontSize: '12px', color: '#64748b' }}>This Week</div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#3b82f6' }}>
            UGX {revenueData.thisWeek.toLocaleString()}
          </div>
        </div>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '16px 20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}>
          <div style={{ fontSize: '12px', color: '#64748b' }}>This Month</div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#8b5cf6' }}>
            UGX {revenueData.thisMonth.toLocaleString()}
          </div>
        </div>
      </div>

      {/* Revenue Trend */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      }}>
        <h3 style={{ marginTop: 0 }}>Revenue Trend (Last 30 Days)</h3>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'flex-end', height: '150px', padding: '10px 0' }}>
          {revenueData.byDay.map((day, index) => {
            const max = Math.max(...revenueData.byDay.map(d => d.revenue), 1);
            const height = (day.revenue / max * 100);
            return (
              <div key={index} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{
                  width: '100%',
                  height: `${Math.max(height, 4)}%`,
                  backgroundColor: '#f97316',
                  borderRadius: '4px',
                  minHeight: '4px',
                  transition: 'height 0.3s',
                }} />
                <div style={{ fontSize: '9px', marginTop: '4px', color: '#64748b' }}>
                  {new Date(day.date).toLocaleDateString('en', { weekday: 'short' })}
                </div>
                <div style={{ fontSize: '8px', fontWeight: 'bold', color: '#f97316' }}>
                  {day.revenue > 0 ? `${(day.revenue/1000).toFixed(0)}k` : '0'}
                </div>
                <div style={{ fontSize: '8px', color: '#94a3b8' }}>
                  {day.rides} rides
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ textAlign: 'center', fontSize: '12px', color: '#64748b', marginTop: '8px' }}>
          Total: UGX {revenueData.byDay.reduce((sum, d) => sum + d.revenue, 0).toLocaleString()}
        </div>
      </div>

      {/* Two Column Layout */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '24px',
        marginBottom: '24px',
      }}>
        {/* Vehicle Revenue */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Revenue by Vehicle</h3>
          {revenueData.byVehicle.length === 0 ? (
            <p style={{ color: '#64748b', textAlign: 'center', padding: '20px' }}>No data</p>
          ) : (
            revenueData.byVehicle.map((item) => {
              const total = revenueData.byVehicle.reduce((sum, i) => sum + i.amount, 0) || 1;
              const percentage = (item.amount / total * 100).toFixed(1);
              const colors = {
                boda: '#f97316',
                boda_electric: '#10b981',
                car: '#3b82f6',
                car_xl: '#8b5cf6',
              };
              return (
                <div key={item.type} style={{ marginBottom: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                    <span>{getVehicleLabel(item.type)}</span>
                    <span>UGX {item.amount.toLocaleString()} ({percentage}%)</span>
                  </div>
                  <div style={{
                    width: '100%',
                    height: '6px',
                    backgroundColor: '#e2e8f0',
                    borderRadius: '4px',
                    overflow: 'hidden',
                  }}>
                    <div style={{
                      width: `${percentage}%`,
                      height: '100%',
                      backgroundColor: colors[item.type] || '#f97316',
                      borderRadius: '4px',
                    }} />
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Peak Hours */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Revenue by Hour</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '4px' }}>
            {[...Array(24)].map((_, hour) => {
              const amount = revenueData.byHour.find(h => h.hour === hour)?.amount || 0;
              const max = Math.max(...revenueData.byHour.map(h => h.amount), 1);
              const intensity = (amount / max * 100);
              return (
                <div key={hour} style={{ textAlign: 'center' }}>
                  <div style={{
                    height: `${Math.max(intensity, 4)}%`,
                    backgroundColor: intensity > 50 ? '#f97316' : intensity > 25 ? '#fbbf24' : '#94a3b8',
                    borderRadius: '4px',
                    minHeight: '4px',
                    transition: 'height 0.3s',
                  }} />
                  <div style={{ fontSize: '8px', marginTop: '4px', color: '#64748b' }}>
                    {hour}h
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: '12px', fontSize: '12px', color: '#64748b' }}>
            💡 Peak hour: {revenueData.byHour.reduce((a, b) => a.amount > b.amount ? a : b).hour}:00
          </div>
        </div>
      </div>

      {/* Recent Transactions */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      }}>
        <h3 style={{ marginTop: 0 }}>Recent Transactions</h3>
        {revenueData.recentTransactions.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#64748b', padding: '20px' }}>No transactions yet</p>
        ) : (
          <div style={{ overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <th style={{ textAlign: 'left', padding: '12px' }}>Ride ID</th>
                  <th style={{ textAlign: 'left', padding: '12px' }}>Rider</th>
                  <th style={{ textAlign: 'left', padding: '12px' }}>Driver</th>
                  <th style={{ textAlign: 'left', padding: '12px' }}>Vehicle</th>
                  <th style={{ textAlign: 'right', padding: '12px' }}>Amount</th>
                  <th style={{ textAlign: 'right', padding: '12px' }}>Date</th>
                </tr>
              </thead>
              <tbody>
                {revenueData.recentTransactions.map((tx) => (
                  <tr key={tx.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '12px' }}>#{tx.id}</td>
                    <td style={{ padding: '12px' }}>{tx.rider}</td>
                    <td style={{ padding: '12px' }}>{tx.driver}</td>
                    <td style={{ padding: '12px' }}>{tx.vehicle}</td>
                    <td style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold', color: '#10b981' }}>
                      UGX {tx.amount.toLocaleString()}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', fontSize: '12px', color: '#64748b' }}>
                      {new Date(tx.date).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ padding: '12px', color: '#64748b', fontSize: '13px', borderTop: '1px solid #e2e8f0', marginTop: '12px' }}>
          Showing {revenueData.recentTransactions.length} recent transactions
        </div>
      </div>
    </div>
  );
}

export default RevenueAnalytics;